<template>
    <div class="dispatch-map-page">
        <div v-if="loadError" class="dm-load-error">
            <div class="mle-title">Dispatch unavailable</div>
            <div class="mle-body">{{ loadError }}</div>
            <button class="mle-close" @click="closeTab">Close tab</button>
        </div>

        <div v-else-if="loading" class="dm-loading">Loading dispatch…</div>

        <template v-else>
        <!-- Header -->
        <header class="dm-header">
            <div class="header-icon">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
            </div>
            <div class="header-text">
                <h1>{{ dispatchName }}</h1>
                <p>{{ locatableSites.length }} locations · {{ formatUllage(totalUllage) }} {{ ullageUnit }} to
                    deliver now · {{ formatUllage(totalPredictedUllage) }} {{ ullageUnit }}
                    predicted{{ forecastLabel }}<span v-if="excludedCount"> · {{ excludedCount }} without
                        coordinates</span></p>
            </div>
            <div class="header-actions">
                <span v-if="saveError" class="save-error">{{ saveError }}</span>
                <span v-else-if="saveMessage" class="save-ok">{{ saveMessage }}</span>
                <button class="save-btn" :disabled="saving" @click="saveDispatch">
                    {{ saving ? 'Saving…' : 'Save Dispatch' }}
                </button>
            </div>
        </header>

        <div class="dm-content">
            <!-- Sidebar -->
            <div class="sidebar">
                <div class="sidebar-header">
                    <button class="delivery-btn" @click="openDeliveryModal">🚚 Delivery Parameters</button>
                    <input class="sidebar-search" type="text" v-model="search"
                        placeholder="Search location, device, operator, product…" />
                </div>
                <div class="well-list">
                    <div v-if="!filteredSites.length" class="no-results">
                        No locations match your search.
                    </div>
                    <div v-for="site in filteredSites" :key="site.containerId"
                        :id="`well-item-${site.containerId}`"
                        class="well-item"
                        :class="{ active: activeId === site.containerId }"
                        :style="activeId === site.containerId ? { borderLeftColor: colorFor(site.product).border } : {}"
                        @click="selectFromList(site.containerId)">
                        <div class="well-dot" :style="siteDotStyle(site)"></div>
                        <div class="well-info">
                            <div class="well-loc">{{ siteLabel(site) }}
                                <span v-if="isUnplaced(site)" class="well-unassigned">unassigned</span>
                            </div>
                            <div class="well-dev">{{ deviceLabel(site) }}</div>
                            <div class="well-product" :style="{ color: colorFor(site.product).border }">
                                {{ site.product || 'No product' }}</div>
                            <div class="well-co">{{ site.operator || 'Unknown operator' }}</div>
                            <div class="well-ullage">Deliver now: {{ formatUllage(site.fillAmount) }}
                                {{ ullageUnit }}</div>
                            <div class="well-predicted">Predicted{{ forecastLabel }}:
                                {{ formatUllage(site.predictedFillAmount) }} {{ ullageUnit }}</div>
                            <div class="well-coords">{{ coordLabel(site) }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Map -->
            <div class="map-area">
                <div ref="mapContainer" class="map-canvas"></div>

                <div class="style-toggle">
                    <button class="style-btn" :class="{ active: currentStyle === 'street' }"
                        @click="setStyle('street')">Street</button>
                    <button class="style-btn" :class="{ active: currentStyle === 'satellite' }"
                        @click="setStyle('satellite')">Satellite</button>
                </div>

                <div v-if="products.length && !plan" class="map-legend">
                    <div class="legend-title">Product</div>
                    <div v-for="p in products" :key="p" class="legend-item">
                        <span class="legend-dot" :style="dotStyle(p)"></span>{{ p }}
                    </div>
                </div>

                <div v-if="!loading && !locatableSites.length" class="map-empty">
                    No sites with coordinates to display.
                </div>

                <!-- Delivery plan results -->
                <div v-if="plan && (plan.trucks.length || plan.unplaced)" class="delivery-results">
                    <div class="dr-header">
                        <span>Delivery Plan</span>
                        <button class="dr-clear" @click="clearPlan">Clear</button>
                    </div>
                    <div class="dr-summary">
                        {{ plan.trucks.length }} truck<span v-if="plan.trucks.length !== 1">s</span> ·
                        {{ formatUllage(plan.plannedUllage) }} of {{ formatUllage(plan.totalUllage) }}
                        {{ ullageUnit }} ·
                        <span v-if="plan.fleetCapacity != null">fleet holds
                            {{ formatUllage(plan.fleetCapacity) }} {{ ullageUnit }}</span>
                        <span v-else>unlimited fleet</span>
                        <span v-if="plan.maxRadiusKm"> · ≤ {{ plan.maxRadiusKm }} km</span>
                        <span v-if="plan.maxSitesPerTruck"> · ≤ {{ plan.maxSitesPerTruck }} sites</span>
                        <span v-if="plan.maxTripsPerTruck > 1"> · ≤ {{ plan.maxTripsPerTruck }} trips/truck</span>
                        <span v-if="plan.products && plan.products.length > 1"> ·
                            {{ plan.products.length }} products</span>
                    </div>
                    <div v-if="plan.unplaced" class="dr-unplaced">
                        ⚠ {{ plan.unplaced }} site<span v-if="plan.unplaced !== 1">s</span> unassigned —
                        {{ unplacedReason }}
                    </div>
                    <div v-for="t in plan.trucks" :key="t.id" class="dr-truck">
                        <div class="dr-truck-top">
                            <span class="dr-swatch" :style="{ background: t.color }"></span>
                            <span class="dr-truck-name">Run {{ t.id }}</span>
                            <button class="dr-print" :title="`Print Run ${t.id} delivery sheet`"
                                @click="printTruck(t)">
                                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 3H6v4h12V3zm1 6H5a3 3 0 0 0-3 3v5h4v4h12v-4h4v-5a3 3 0 0 0-3-3zm-3 10H8v-5h8v5zm2.5-6a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                                </svg>
                            </button>
                            <span class="dr-truck-cap">
                                <template v-if="t.mode === 'deck'">{{ t.toteSlots }} totes</template>
                                <template v-else>{{ formatUllage(t.capacity) }} {{ ullageUnit }}</template>
                            </span>
                        </div>
                        <div class="dr-truck-meta">
                            {{ t.sites.length }} site<span v-if="t.sites.length !== 1">s</span> ·
                            {{ formatUllage(t.load) }} {{ ullageUnit }}<span
                                v-if="t.utilization != null"> ({{ utilizationPct(t) }}%)</span> ·
                            r {{ t.radiusKm.toFixed(1) }} km<span v-if="t.trips > 1"
                                :class="{ 'dr-warn': t.trips > plan.maxTripsPerTruck }"> ·
                                {{ t.trips }} trips</span><span v-if="t.compartments > 1"> ·
                                {{ t.compartmentsUsed }}/{{ t.compartmentBudget }} compartments</span>
                        </div>
                        <div v-if="t.products.length > 1" class="dr-products">
                            <span v-for="p in t.products" :key="p.product" class="dr-prod"
                                :style="{ borderLeftColor: colorFor(p.product).border }">
                                {{ p.product }} · {{ formatUllage(p.load) }} {{ ullageUnit }}<span
                                    v-if="t.compartments > 1"> · {{ p.compartments }}c</span>
                            </span>
                        </div>
                        <div v-if="t.overCapacity" class="dr-warn dr-overcap">
                            ⚠ This load does not fit the truck's compartments.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delivery Parameters modal -->
        <div v-if="showDeliveryModal" class="dm-modal-overlay" @click.self="closeDeliveryModal">
            <div class="dm-modal">
                <h3>Delivery Parameters</h3>
                <p class="modal-sub">
                    {{ locatableSites.length }} sites · {{ formatUllage(totalUllage) }} {{ ullageUnit }}
                    to deliver now<span v-if="forecastDays">, {{ formatUllage(totalPredictedUllage) }}
                        {{ ullageUnit }} predicted{{ forecastLabel }}</span>.
                    Loads are planned from the current amount.
                </p>
                <label class="modal-label">{{ isTotes ? 'Deck Trucks' : 'Tankers' }}
                    <span class="modal-optional">blank quantity = unlimited</span></label>
                <div class="fleet-table">
                    <div class="fleet-head">
                        <span v-if="isTotes">Totes per truck</span>
                        <span v-else>Capacity ({{ ullageUnit }})</span>
                        <span>How many</span>
                        <span></span>
                    </div>
                    <div v-for="(tier, i) in fleet" :key="i" class="fleet-row">
                        <!-- A tote run measures a truck in deck slots; a tanker run in
                             litres. Which one this is was settled on the Parameters step. -->
                        <input v-if="isTotes" class="modal-input" type="number" min="1" step="1"
                            v-model.number="tier.toteSlots" placeholder="e.g. 8"
                            title="How many totes fit on the deck"
                            @keyup.enter="calculateDelivery" />
                        <input v-else class="modal-input" type="number" min="1" step="any"
                            v-model.number="tier.capacity" placeholder="e.g. 30000"
                            @keyup.enter="calculateDelivery" />
                        <input class="modal-input" type="number" min="1" step="1"
                            v-model.number="tier.count" placeholder="∞"
                            @keyup.enter="calculateDelivery" />
                        <button class="fleet-remove" :disabled="fleet.length === 1"
                            title="Remove this truck size" @click="removeFleetRow(i)">×</button>
                    </div>
                </div>
                <button class="fleet-add" @click="addFleetRow">
                    + Add {{ isTotes ? 'deck size' : 'truck size' }}
                </button>

                <!-- Hidden for now — the fleet is the only thing operators tune, and the
                     planner still honours all four. maxRadiusKm / maxSitesPerTruck stay
                     blank (= no limit), maxTripsPerTruck blank (= 1 run per truck), and
                     optimizeEffort is pinned to its maximum in data(). Un-comment these
                     blocks to put the controls back.

                <label class="modal-label">Max Cluster Radius (km) <span class="modal-optional">optional</span></label>
                <input class="modal-input" type="number" min="0" step="any" v-model.number="maxRadiusKm"
                    placeholder="blank = no limit" @keyup.enter="calculateDelivery" />

                <label class="modal-label">Max Sites per Truck <span class="modal-optional">optional</span></label>
                <input class="modal-input" type="number" min="1" step="1" v-model.number="maxSitesPerTruck"
                    placeholder="blank = no limit" @keyup.enter="calculateDelivery" />

                <label class="modal-label">Max Trips per Truck <span class="modal-optional">optional</span></label>
                <input class="modal-input" type="number" min="1" step="1" v-model.number="maxTripsPerTruck"
                    placeholder="blank = 1 run per truck" @keyup.enter="calculateDelivery" />

                <label class="modal-label">Optimize — tighter clusters
                    <span class="modal-optional">{{ optimizeEffort }}</span></label>
                <input class="modal-slider" type="range" min="0" max="100" step="5"
                    v-model.number="optimizeEffort" />
                <div class="slider-ends"><span>Faster</span><span>Tighter</span></div>
                -->

                <p v-if="fleetSummary.tiers.length" class="modal-hint">
                    Fleet holds <strong>{{ fleetCapacityLabel }}</strong> ·
                    minimum trucks needed: <strong>{{ fleetSummary.trucksNeeded }}</strong>
                    <span v-if="maxRadiusKm > 0 || maxSitesPerTruck > 0"> (radius / site limits may add more)</span>
                </p>
                <p v-if="fleetSummary.tankerOnly.length && fleetSummary.hasDeck" class="modal-hint">
                    No tote size set on the Supply step for
                    <strong>{{ fleetSummary.tankerOnly.join(', ') }}</strong> &mdash;
                    {{ fleetSummary.tankerOnly.length === 1 ? 'it can' : 'they can' }} only
                    travel by tanker.
                </p>
                <p v-if="fleetSummary.uncarryable.length" class="modal-warn">
                    Nothing in this fleet can carry
                    <strong>{{ fleetSummary.uncarryable.join(', ') }}</strong>: no tote size on
                    the Supply step and no tanker in the fleet. Those sites will be left
                    unassigned.
                </p>
                <p v-if="fleetSummary.productCount > 1" class="modal-hint">
                    <strong>{{ fleetSummary.productCount }} products</strong> to deliver. A compartment
                    carries one product, so these need
                    <strong>{{ fleetSummary.slotsNeeded }}</strong> compartment-loads in total —
                    a truck covers {{ compartmentsPerTruckLabel }} of them per run.
                </p>
                <p v-if="fleetSummary.shortfall > 0" class="modal-warn">
                    The fleet is {{ formatUllage(fleetSummary.shortfall) }} {{ ullageUnit }} short — some sites
                    won't be assigned to a truck.
                </p>
                <p v-if="modalError" class="modal-error">{{ modalError }}</p>
                <div class="modal-actions">
                    <button class="btn-secondary" @click="closeDeliveryModal">Cancel</button>
                    <button class="btn-primary" @click="calculateDelivery">Calculate &amp; Plan</button>
                </div>
            </div>
        </div>
        </template>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { planDeliveries, summarizeFleet } from './delivery-planner';

const ULLAGE_UNIT = 'L';
const ZONE_PAD = 0.013; // ~1.3km padding around each truck's bounding box

// Color palette assigned per distinct operator (generalizes the original
// three hard-coded operator colors).
const PALETTE = [
    { fill: '#388bd6', border: '#7ab8f5' },
    { fill: '#2ec48c', border: '#5ddba8' },
    { fill: '#e8a838', border: '#f5c96a' },
    { fill: '#b86fc9', border: '#d6a0e3' },
    { fill: '#d6685a', border: '#f0938a' },
    { fill: '#4fb3bf', border: '#85d6df' },
    { fill: '#c9a227', border: '#e6c65c' },
    { fill: '#7e8bd6', border: '#a6b0ec' },
];
const FALLBACK_COLOR = { fill: '#888', border: '#bbb' };
// Sites a plan couldn't fit onto any truck — deliberately drab so they read as
// "not part of the plan" next to the saturated truck colours.
const UNPLACED_COLOR = { fill: '#4a5866', border: '#8b9bab' };

const STYLES = {
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    street: 'mapbox://styles/mapbox/streets-v12',
};

export default {
    name: 'DispatchMap',
    data() {
        return {
            map: null,
            markers: new Map(), // containerId -> { marker, el, popup }
            activeId: null,
            search: '',
            currentStyle: 'satellite',
            loading: true,
            loadError: null,
            ullageUnit: ULLAGE_UNIT,
            showDeliveryModal: false,
            // Truck fleet: one row per capacity tier. A blank `count` means
            // "as many as needed" of that size.
            fleet: [{ capacity: null, toteSlots: null, count: null }],
            // True once a fleet saved with this dispatch has been restored, which
            // is what licenses replanning on open.
            restoredDeliveryParams: false,
            maxRadiusKm: null,
            maxSitesPerTruck: null,
            maxTripsPerTruck: null,
            // Pinned to the maximum while the Optimize slider is hidden.
            optimizeEffort: 100,
            modalError: null,
            plan: null,
            saving: false,
            saveMessage: null,
            saveError: null,
            saveMessageTimer: null,
        };
    },
    computed: {
        sites() {
            return this.$store.getters['dispatch/getCurrentDispatchSitesWithCoords'];
        },
        dispatchName() {
            return this.$store.getters['dispatch/getCurrentDispatch'].name || 'Dispatch Sites';
        },
        dispatchParams() {
            return this.$store.getters['dispatch/getCurrentDispatchParameters'] || {};
        },
        locatableSites() {
            return this.sites.filter((s) => this.hasCoords(s));
        },
        excludedCount() {
            return this.sites.length - this.locatableSites.length;
        },
        totalUllage() {
            return this.locatableSites.reduce((sum, s) => sum + (Number(s.fillAmount) || 0), 0);
        },
        // What the same sites will need on the dispatch date, once Injection Avg
        // has drawn the tanks down.
        totalPredictedUllage() {
            return this.locatableSites.reduce(
                (sum, s) => sum + (Number(s.predictedFillAmount) || 0),
                0
            );
        },
        forecastDays() {
            return this.$store.getters['dispatch/getDispatchForecastDays'];
        },
        // " in 12d" — or nothing at all when the dispatch date is today/unset, where
        // the prediction and the current amount are the same number.
        forecastLabel() {
            return this.forecastDays ? ` in ${this.forecastDays}d` : '';
        },
        // Litres per product across the located sites. A compartment carries one
        // product, so the planner needs the split, not just the total.
        productLoads() {
            const loads = {};
            this.locatableSites.forEach((s) => {
                const key = s.product || 'No product';
                loads[key] = (loads[key] || 0) + (Number(s.fillAmount) || 0);
            });
            return loads;
        },
        // Litres per tote for each product, as captured on the Supply step. A deck
        // truck can only carry a product that has one.
        toteSizes() {
            const supply = this.$store.getters['dispatch/getCurrentDispatchSupply'] || {};
            const byProduct = supply.byProduct || {};
            const sizes = {};
            Object.keys(byProduct).forEach((product) => {
                const size = Number(byProduct[product] && byProduct[product].toteSize) || 0;
                if (size > 0) sizes[product] = size;
            });
            return sizes;
        },
        // How this dispatch is delivered, decided on the Parameters step.
        deliveryMethod() {
            return this.$store.getters['dispatch/getDeliveryMethod'];
        },
        isTotes() {
            return this.deliveryMethod === 'totes';
        },
        // The fleet rows in the shape the planner wants. Every truck in a dispatch is
        // the same kind, and a tanker is single-shell — one product per run.
        fleetRows() {
            return this.fleet.map((t) =>
                this.isTotes
                    ? { mode: 'deck', toteSlots: t.toteSlots, count: t.count }
                    : { mode: 'tanker', capacity: t.capacity, compartments: 1, count: t.count }
            );
        },
        // Fleet arithmetic (capacity, trucks needed, shortfall) shared with the
        // planner so the modal preview can't drift from the real plan.
        fleetSummary() {
            return summarizeFleet(
                this.fleetRows, this.maxTripsPerTruck, this.totalUllage, this.productLoads,
                this.toteSizes
            );
        },
        // How many compartment-loads one truck covers per plan — a range when the
        // fleet mixes different compartment counts.
        compartmentsPerTruckLabel() {
            const trips = Math.max(1, Math.floor(Number(this.maxTripsPerTruck) || 1));
            const counts = this.fleetSummary.tiers.map((t) => t.compartments * trips);
            if (!counts.length) return '0';
            const min = Math.min(...counts);
            const max = Math.max(...counts);
            return min === max ? String(min) : `${min}–${max}`;
        },
        fleetCapacityLabel() {
            const s = this.fleetSummary;
            if (s.unlimited) return 'an unlimited amount';
            return `${this.formatUllage(s.capacity)} ${this.ullageUnit}`;
        },
        // Why sites were left off the plan. Short on litres and short on vehicles are
        // different problems: the radius / max-sites caps force smaller loads, so a
        // fleet with plenty of capacity can still run out of trucks.
        unplacedReason() {
            if (!this.plan || !this.plan.unplaced) return '';
            if (this.plan.shortfall > 0) {
                return `the fleet is ${this.formatUllage(this.plan.shortfall)} ${this.ullageUnit} short on capacity.`;
            }
            return 'the fleet ran out of trucks — the radius / max-sites limits cap how much each one can take.';
        },
        // containerIds a plan couldn't assign to any truck.
        unplacedIds() {
            const ids = new Set();
            if (this.plan) {
                (this.plan.unplacedSites || []).forEach((s) => ids.add(s.containerId));
            }
            return ids;
        },
        products() {
            const set = new Set();
            this.locatableSites.forEach((s) => set.add(s.product || 'No product'));
            return Array.from(set).sort();
        },
        productColorMap() {
            const map = {};
            this.products.forEach((p, i) => {
                map[p] = PALETTE[i % PALETTE.length];
            });
            return map;
        },
        // containerId -> truck color, when a delivery plan is active.
        siteTruckColors() {
            const map = {};
            if (this.plan) {
                this.plan.trucks.forEach((t) => {
                    t.sites.forEach((s) => {
                        map[s.containerId] = t.color;
                    });
                });
            }
            return map;
        },
        filteredSites() {
            const q = this.search.trim().toLowerCase();
            if (!q) return this.locatableSites;
            return this.locatableSites.filter((s) =>
                [this.siteLabel(s), this.deviceLabel(s), s.operator, s.product]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(q)
            );
        },
    },
    async mounted() {
        const ok = await this.ensureDispatchLoaded();
        if (ok) {
            this.hydrateDeliveryParams();
            // Coordinates are merged in from the containers module.
            try {
                await this.$store.dispatch('containers/loadContainers');
            } catch (error) {
                console.warn('dispatch-map: failed to load container coordinates.', error);
            }
        }
        this.loading = false;
        if (ok) this.$nextTick(() => this.initMap());
    },
    unmounted() {
        if (this.saveMessageTimer) clearTimeout(this.saveMessageTimer);
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    methods: {
        // Make sure the dispatch named by the ?id URL param is the current one:
        // use the cache/store if we already have it, otherwise fetch it from the
        // API. Sets loadError (and returns false) if it can't be found.
        async ensureDispatchLoaded() {
            const id = this.$route.query.id;
            // Fast path: rehydrate from the cache the summary wrote (new tab).
            this.$store.dispatch('dispatch/restoreCachedDispatch');
            const current = this.$store.getters['dispatch/getCurrentDispatch'];

            if (id == null || id === '') {
                if (current.selectedSites && current.selectedSites.length) return true;
                this.loadError = 'No dispatch was specified.';
                return false;
            }

            if (
                String(current.dispatchId) === String(id) &&
                current.selectedSites &&
                current.selectedSites.length
            ) {
                return true; // already loaded (cache or a prior load)
            }

            const found = await this.$store.dispatch('dispatch/loadDispatchById', id);
            if (!found) {
                this.loadError = `Dispatch #${id} could not be found.`;
                return false;
            }
            return true;
        },
        closeTab() {
            window.close();
        },
        hasCoords(s) {
            const lat = parseFloat(s.latitude);
            const lng = parseFloat(s.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        },
        colorFor(product) {
            return this.productColorMap[product || 'No product'] || FALLBACK_COLOR;
        },
        // Pin color: the delivering truck's color when a plan exists, a drab grey
        // when a plan exists but couldn't fit this site, otherwise the product color.
        colorForSite(site) {
            const truckColor = site && this.siteTruckColors[site.containerId];
            if (truckColor) {
                return { fill: truckColor, border: this.lighten(truckColor, 0.45) };
            }
            if (this.plan && site && this.unplacedIds.has(site.containerId)) {
                return UNPLACED_COLOR;
            }
            return this.colorFor(site ? site.product : null);
        },
        isUnplaced(site) {
            return !!this.plan && this.unplacedIds.has(site.containerId);
        },
        utilizationPct(truck) {
            return Math.round((truck.utilization || 0) * 100);
        },
        lighten(hex, amount) {
            const h = hex.replace('#', '');
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            const mix = (c) => Math.round(c + (255 - c) * amount);
            return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
        },
        dotStyle(product) {
            const c = this.colorFor(product);
            return { background: c.fill, borderColor: c.border };
        },
        // Sidebar dot: mirrors the map pin, so it follows the truck colours once
        // a plan exists and falls back to the product colour otherwise.
        siteDotStyle(site) {
            const c = this.colorForSite(site);
            return { background: c.fill, borderColor: c.border };
        },
        badgeStyle(product) {
            const c = this.colorFor(product);
            return {
                background: this.hexToRgba(c.fill, 0.2),
                color: c.border,
                border: `1px solid ${this.hexToRgba(c.fill, 0.4)}`,
            };
        },
        siteLabel(s) {
            return s.surface || s.alias || `Site ${s.containerId}`;
        },
        deviceLabel(s) {
            if (s.devices) return `Device ${s.devices}`;
            if (s.serialnum) return `Device #${s.serialnum}`;
            return `Container ${s.containerId}`;
        },
        coordLabel(s) {
            const lat = parseFloat(s.latitude);
            const lng = parseFloat(s.longitude);
            return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        },
        formatUllage(value) {
            return Math.round(Number(value) || 0).toLocaleString();
        },
        fmtDate(value) {
            if (!value) return '—';
            const d = new Date(value);
            return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
        },
        initMap() {
            const container = this.$refs.mapContainer;
            if (!container) return;

            mapboxgl.accessToken = this.$store.state.config.mapbox.accessToken;

            const cfgCenter = this.$store.state.config.mapbox.defaultCenter || [53.5461, -113.4937];
            // config stores [lat, lng]; mapbox wants [lng, lat]
            const fallbackCenter = [cfgCenter[1], cfgCenter[0]];

            this.map = new mapboxgl.Map({
                container,
                style: STYLES[this.currentStyle],
                center: fallbackCenter,
                zoom: this.$store.state.config.mapbox.defaultZoom || 5,
                attributionControl: true,
            });

            this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
            // Distance scale bar so users can estimate distances on the map.
            this.map.addControl(
                new mapboxgl.ScaleControl({ maxWidth: 140, unit: 'metric' }),
                'bottom-right'
            );

            this.map.on('load', () => {
                this.addMarkers();
                this.fitToSites();
                this.autoPlanFromSaved();
            });
        },
        addMarkers() {
            if (!this.map) return;
            this.markers = new Map();

            this.locatableSites.forEach((site) => {
                const pinColor = this.colorForSite(site);
                const productColor = this.colorFor(site.product);
                const el = document.createElement('div');
                el.className = 'dm-marker';
                el.style.background = pinColor.fill;
                el.style.borderColor = pinColor.border;

                const popup = new mapboxgl.Popup({ offset: 14, maxWidth: '280px' }).setHTML(
                    this.popupHtml(site, productColor)
                );

                const id = site.containerId;
                popup.on('open', () => this.setActive(id, false));
                popup.on('close', () => {
                    if (this.activeId === id) this.setActive(null, false);
                });

                const marker = new mapboxgl.Marker({ element: el })
                    .setLngLat([parseFloat(site.longitude), parseFloat(site.latitude)])
                    .setPopup(popup)
                    .addTo(this.map);

                this.markers.set(id, { marker, el, popup });
            });
        },
        // Recolor existing pins in place (e.g. after a plan is created/cleared)
        // without rebuilding markers/popups.
        refreshMarkerColors() {
            this.markers.forEach((entry, id) => {
                const site = this.locatableSites.find((s) => s.containerId === id);
                const c = this.colorForSite(site);
                entry.el.style.background = c.fill;
                entry.el.style.borderColor = c.border;
            });
        },
        popupHtml(site, color) {
            const area = site.area
                ? `<div class="popup-coords">Area: ${this.escape(site.area)}</div>`
                : '';
            return `
                <div class="popup-inner">
                    <div class="popup-location">${this.escape(this.siteLabel(site))}</div>
                    <div class="popup-device">${this.escape(this.deviceLabel(site))}</div>
                    <div class="popup-company" style="color:${color.border}">${this.escape(site.product || 'No product')}</div>
                    <div class="popup-operator">${this.escape(site.operator || 'Unknown operator')}</div>
                    <div class="popup-ullage">Deliver now: ${this.formatUllage(site.fillAmount)} ${ULLAGE_UNIT}</div>
                    <div class="popup-predicted">Predicted${this.escape(this.forecastLabel)}: ${this.formatUllage(site.predictedFillAmount)} ${ULLAGE_UNIT}</div>
                    <div class="popup-coords">${this.coordLabel(site)}</div>
                    ${area}
                </div>`;
        },
        escape(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        },
        setActive(id, flyTo = true) {
            // Deactivate previous marker element.
            if (this.activeId !== null && this.markers.has(this.activeId)) {
                this.markers.get(this.activeId).el.classList.remove('active-marker');
            }

            this.activeId = id;
            if (id === null) return;

            const entry = this.markers.get(id);
            if (entry) entry.el.classList.add('active-marker');

            const listItem = document.getElementById(`well-item-${id}`);
            if (listItem) listItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

            if (flyTo && entry) {
                const site = this.locatableSites.find((s) => s.containerId === id);
                if (site) {
                    this.map.flyTo({
                        center: [parseFloat(site.longitude), parseFloat(site.latitude)],
                        zoom: 13,
                        speed: 1.4,
                        essential: true,
                    });
                    this.map.once('moveend', () => entry.marker.togglePopup());
                }
            }
        },
        selectFromList(id) {
            // Close any open popups first.
            this.markers.forEach(({ marker }) => {
                if (marker.getPopup().isOpen()) marker.togglePopup();
            });
            this.setActive(id, true);
        },
        setStyle(style) {
            if (style === this.currentStyle || !this.map) return;
            this.currentStyle = style;
            this.markers.forEach(({ marker }) => marker.remove());
            this.markers = new Map();
            this.map.setStyle(STYLES[style]);
            this.map.once('style.load', () => {
                this.addMarkers();
                // setStyle wipes custom sources/layers, so redraw the zones.
                this.drawZones();
            });
        },
        fitToSites() {
            if (!this.map || !this.locatableSites.length) return;
            if (this.locatableSites.length === 1) {
                const s = this.locatableSites[0];
                this.map.flyTo({
                    center: [parseFloat(s.longitude), parseFloat(s.latitude)],
                    zoom: 12,
                });
                return;
            }
            const bounds = new mapboxgl.LngLatBounds();
            this.locatableSites.forEach((s) =>
                bounds.extend([parseFloat(s.longitude), parseFloat(s.latitude)])
            );
            this.map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 0 });
        },
        hexToRgba(hex, alpha) {
            const h = hex.replace('#', '');
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        },
        // ---- Delivery planning ----
        openDeliveryModal() {
            this.modalError = null;
            this.showDeliveryModal = true;
        },
        closeDeliveryModal() {
            this.showDeliveryModal = false;
        },
        addFleetRow() {
            this.fleet.push({ capacity: null, toteSlots: null, count: null });
        },
        removeFleetRow(index) {
            if (this.fleet.length === 1) return;
            this.fleet.splice(index, 1);
        },
        // The fleet + clustering settings, in the shape stored on the dispatch.
        deliveryParams() {
            return {
                fleet: this.fleetSummary.tiers.map((t) => ({
                    capacity: t.capacity,
                    toteSlots: t.toteSlots,
                    count: t.count === Infinity ? null : t.count,
                })),
                maxRadiusKm: this.maxRadiusKm,
                maxSitesPerTruck: this.maxSitesPerTruck,
                maxTripsPerTruck: this.maxTripsPerTruck,
                optimizeEffort: this.optimizeEffort,
            };
        },
        // Restore the fleet/clustering settings saved with this dispatch (the map
        // opens in its own tab, so this is the only way they survive).
        hydrateDeliveryParams() {
            const dp = this.$store.getters['dispatch/getCurrentDispatchDeliveryParams'] || {};
            if (Array.isArray(dp.fleet) && dp.fleet.length) {
                this.fleet = dp.fleet.map((t) => ({
                    capacity: t.capacity ?? null,
                    toteSlots: t.toteSlots ?? null,
                    count: t.count ?? null,
                }));
                this.restoredDeliveryParams = true;
            }
            // Only the fleet is restored. The limits and the optimize effort no longer
            // have controls in the modal, so rehydrating them would leave a dispatch
            // saved earlier planning against an invisible 60 km radius cap — or at a
            // lower optimize effort — with nothing on screen to explain it or change
            // it. They stay at the current defaults and are re-saved on the next plan.
            // Restore these four lines alongside the modal inputs if the controls
            // come back.
            //
            // if (dp.maxRadiusKm != null) this.maxRadiusKm = dp.maxRadiusKm;
            // if (dp.maxSitesPerTruck != null) this.maxSitesPerTruck = dp.maxSitesPerTruck;
            // if (dp.maxTripsPerTruck != null) this.maxTripsPerTruck = dp.maxTripsPerTruck;
            // if (dp.optimizeEffort != null) this.optimizeEffort = dp.optimizeEffort;
        },
        // Runs the planner against the current settings and paints the result.
        // Returns null on success, or the reason it could not run — the caller
        // decides whether that belongs on screen.
        buildPlan() {
            if (!this.fleetSummary.tiers.length) {
                return this.isTotes
                    ? 'Add at least one deck size with a tote count greater than zero.'
                    : 'Add at least one truck size with a capacity greater than zero.';
            }
            if (!this.totalUllage) {
                return 'No fill amount to deliver for the located sites.';
            }
            this.plan = planDeliveries(this.locatableSites, this.fleetSummary.tiers, {
                // On a tote run each drop is rounded to whole totes where the
                // site's delivery range allows it; a partial tote is only used
                // where whole ones cannot satisfy the site.
                wholeTotes: this.isTotes,
                toteSizes: this.toteSizes,
                maxRadiusKm: this.maxRadiusKm,
                maxSitesPerTruck: this.maxSitesPerTruck,
                maxTripsPerTruck: this.maxTripsPerTruck,
                optimizeEffort: this.optimizeEffort,
            });
            this.logPlanSummary();
            this.drawZones();
            this.refreshMarkerColors();
            return null;
        },
        // Per-product breakdown of the plan, to the console: what is being
        // delivered, how many totes carry it, and how much room is left in the
        // last one. Leftover is the give-away figure -- 0 means every tote comes
        // back empty, anything else is the part-used tote.
        logPlanSummary() {
            if (!this.plan) return;

            const alloc = this.plan.toteAllocation || {};
            // Placed load per product, which can be below the allocated total
            // when a site could not be fitted onto any truck.
            const placed = {};
            (this.plan.trucks || []).forEach((truck) => {
                (truck.sites || []).forEach((site) => {
                    const product = site.product || 'No product';
                    const amount = site.plannedAmount != null
                        ? Number(site.plannedAmount)
                        : Number(site.fillAmount) || 0;
                    placed[product] = (placed[product] || 0) + amount;
                });
            });

            const products = Array.from(
                new Set([...Object.keys(alloc), ...Object.keys(placed)])
            ).sort();

            const rows = products.map((product) => {
                const a = alloc[product] || {};
                const toteSize = Number(a.toteSize) || 0;
                const totes = Number(a.totes) || 0;
                const total = a.total != null ? Number(a.total) : (placed[product] || 0);
                // Capacity loaded minus litres delivered = what stays in the
                // final tote. Zero whenever the total lands on a tote boundary.
                const leftover = toteSize > 0 && totes > 0 ? totes * toteSize - total : 0;

                return {
                    Product: product,
                    'Total (L)': total,
                    'Planned on trucks (L)': placed[product] || 0,
                    'Tote Size (L)': toteSize || '—',
                    'Totes Used': totes || '—',
                    'Leftover in Final Tote (L)': toteSize > 0 ? leftover : '—',
                    'All Totes Empty': toteSize > 0 ? (leftover === 0 ? 'yes' : 'no') : '—',
                };
            });

            /* eslint-disable no-console */
            console.log(
                `[dispatch] delivery plan — ${this.isTotes ? 'totes' : 'tanker'} run, ` +
                `${this.plan.truckCount} truck(s), ${this.plan.unplaced} site(s) unplaced`
            );
            if (!rows.length) {
                console.log('[dispatch] no products in this plan.');
            } else if (console.table) {
                console.table(rows);
            } else {
                rows.forEach((r) => console.log('[dispatch]', r));
            }
            /* eslint-enable no-console */
        },
        // Describes the run's tote allocation. Totes are filled from across
        // several sites, so wholeness is a property of each product's total, not
        // of any one site's drop.
        toteAllocationNote() {
            const alloc = (this.plan && this.plan.toteAllocation) || {};
            const products = Object.keys(alloc);
            if (!products.length) return '';

            const parts = products.map((product) => {
                const a = alloc[product];
                const totes = a.totes || 0;
                const label = `${product}: ${totes} tote${totes === 1 ? '' : 's'}`;
                return a.fullTotes ? label : `${label} (last one part-used)`;
            });
            const anyPartial = products.some((product) => !alloc[product].fullTotes);
            return (
                `Loads were shared out so each product's total fills whole totes &mdash; ${parts.join('; ')}.` +
                (anyPartial
                    ? ' A part-used tote means no whole number of totes fell inside the combined'
                      + ' minimum-to-maximum range for that product.'
                    : '')
            );
        },
        calculateDelivery() {
            this.modalError = this.buildPlan();
            if (!this.modalError) this.showDeliveryModal = false;
        },
        // A dispatch that was saved with delivery settings shows its drop-offs as
        // soon as the map opens, instead of making the user re-open the modal and
        // press Calculate to see a plan that was already decided.
        //
        // Called from the map's `load` handler: drawZones defers itself until the
        // style is ready, but refreshMarkerColors needs the markers, and those are
        // created there.
        autoPlanFromSaved() {
            if (!this.restoredDeliveryParams || this.plan) return;
            const reason = this.buildPlan();
            if (reason) {
                // Nothing is on screen to show this against — the modal is closed —
                // so leave the map unplanned and let the user open it themselves.
                console.warn('dispatch-map: saved delivery settings could not be replanned.', reason);
            }
        },
        // Persist the dispatch (including the fleet settings) via the dispatch
        // store's PUT. Falls back to a create when this dispatch has no id yet.
        async saveDispatch() {
            this.saving = true;
            this.saveError = null;
            this.saveMessage = null;
            if (this.saveMessageTimer) clearTimeout(this.saveMessageTimer);
            try {
                this.$store.dispatch('dispatch/setDeliveryParams', this.deliveryParams());
                await this.$store.dispatch('dispatch/updateDispatch');
                this.saveMessage = 'Saved';
                this.saveMessageTimer = setTimeout(() => {
                    this.saveMessage = null;
                }, 4000);
            } catch (error) {
                this.saveError = (error && error.message) || 'Save failed.';
            } finally {
                this.saving = false;
            }
        },
        clearPlan() {
            this.plan = null;
            this.removeZoneLayers();
            this.refreshMarkerColors();
        },
        // ---- Per-truck delivery sheet ----
        // Same approach as the summary's Create PDF: open a window, write a
        // self-contained document, print it. Everything needed is already in the
        // plan, so the window can be opened and filled in one synchronous pass
        // (no await before window.open, which popup blockers would kill).
        printTruck(truck) {
            const win = window.open('', '_blank');
            if (!win) return;

            const params = this.dispatchParams;
            const esc = (v) => this.escape(v);
            const num = (v) => this.formatUllage(v);

            // What this truck actually loads. On a multi-compartment run the driver
            // needs the per-product split, not the dispatch's product filter.
            const truckProducts = (truck.products || [])
                .map((entry) => {
                    const amount = `${num(entry.load)} ${ULLAGE_UNIT}`;
                    const comps = truck.compartments > 1 ? ` in ${entry.compartments}` : '';
                    return `${entry.product} — ${amount}${comps}`;
                })
                .join('; ') || '—';
            // Property keys on the dispatch rows are lower-cased; tolerate the
            // other spellings seen elsewhere in the app.
            const downhole = (s) => {
                const v = s['downhole lsd'] != null ? s['downhole lsd']
                    : s.downholeLSD != null ? s.downholeLSD
                        : s['Downhole LSD'];
                return v != null && String(v).trim() !== '' ? v : '';
            };

            // Alphabetical by site name — a predictable checklist order. The planner
            // groups by proximity but doesn't produce a driving route, so this
            // deliberately doesn't imply one.
            const sites = [...truck.sites].sort((a, b) =>
                this.siteLabel(a).localeCompare(this.siteLabel(b))
            );

            // plannedAmount is what the plan decided to load for this site --
            // on a tote run that is rounded to whole totes, so it can differ
            // from the top of the site's range.
            const planned = (site) =>
                site.plannedAmount != null ? Number(site.plannedAmount) : Number(site.fillAmount) || 0;
            const totalFill = sites.reduce((sum, s) => sum + planned(s), 0);
            const totalPredicted = sites.reduce(
                (sum, s) => sum + (Number(s.predictedFillAmount) || 0),
                0
            );

            const rows = sites
                .map(
                    (s, i) =>
                        `<tr><td class="num">${i + 1}</td>` +
                        `<td>${esc(s.operator || '')}</td>` +
                        `<td>${esc(s.area || '')}</td>` +
                        `<td>${esc(this.siteLabel(s))}</td>` +
                        `<td>${esc(downhole(s))}</td>` +
                        `<td>${esc(this.deviceLabel(s))}</td>` +
                        `<td>${esc(s.product || '')}</td>` +
                        `<td class="num">${num(planned(s))}</td>` +
                        `<td class="num blank"></td>` +
                        `<td class="num">${num(s.predictedFillAmount)}</td></tr>`
                )
                .join('');

            const meta = (label, value) =>
                `<div class="meta"><strong>${label}:</strong> ${value}</div>`;
            const days = this.forecastDays;

            win.document.open();
            win.document.write(`
                <html>
                <head>
                    <title>Run ${truck.id} - ${esc(this.dispatchName)}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 34px; color: #222; }
                        h1 { font-size: 19px; margin: 0 0 2px; }
                        h2 { font-size: 15px; margin: 0 0 14px; color: #555; font-weight: normal; }
                        .band { height: 8px; border-radius: 4px; margin-bottom: 14px;
                                background: ${esc(truck.color)}; }
                        .cols { display: flex; gap: 40px; flex-wrap: wrap; }
                        .meta { margin: 4px 0; font-size: 13px; }
                        table { border-collapse: collapse; width: 100%; margin-top: 16px; }
                        th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left;
                                 font-size: 12px; }
                        th { background: #f3f3f3; }
                        td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
                        /* Actual Amount is left empty for the delivery driver to write in. */
                        td.blank { min-width: 80px; background: #fff; }
                        tfoot td { font-weight: bold; background: #fafafa; }
                        .note { margin-top: 14px; font-size: 11px; color: #666; font-style: italic; }
                        @media print { body { margin: 12px; } }
                    </style>
                </head>
                <body>
                    <div class="band"></div>
                    <h1>Run ${truck.id} — Delivery Sheet</h1>
                    <h2>${esc(this.dispatchName)}</h2>
                    <div class="cols">
                        <div>
                            ${meta('Dispatch Date', esc(this.fmtDate(params.dispatchDate)))}
                            ${meta('Product', esc(truckProducts))}
                            ${meta('Sites on this truck', sites.length)}
                        </div>
                        <div>
                            ${meta('Truck', truck.mode === 'deck'
                              ? `${truck.toteSlots} tote slots`
                              : `${num(truck.capacity)} ${ULLAGE_UNIT}`)}
                            ${meta('Load', truck.utilization != null
                              ? `${num(truck.load)} ${ULLAGE_UNIT} (${this.utilizationPct(truck)}% of capacity)`
                              : `${num(truck.load)} ${ULLAGE_UNIT}`)}
                            ${meta('Trips', truck.trips)}
                            ${meta('Cluster Radius', `${truck.radiusKm.toFixed(1)} km`)}
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th class="num">#</th>
                                <th>Operator</th><th>Area</th><th>Site</th>
                                <th>Downhole LSD</th>
                                <th>Device</th><th>Product</th>
                                <th class="num">Load (L)</th>
                                <th class="num">Actual Amount (L)</th>
                                <th class="num">Predicted Fill Amount (L)</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                        <tfoot>
                            <tr>
                                <td colspan="7">Total</td>
                                <td class="num">${num(totalFill)}</td>
                                <td class="num blank"></td>
                                <td class="num">${num(totalPredicted)}</td>
                            </tr>
                        </tfoot>
                    </table>
                    <p class="note">Load is what this site is planned to receive, within its
                        minimum-to-maximum delivery range. ${this.toteAllocationNote()}
                        Predicted Fill Amount is each site's requirement on the dispatch
                        date${days ? ` (${days} day${days === 1 ? '' : 's'} out)` : ''}, projected from its
                        current volume and Injection Avg. Site order is alphabetical, not a driving
                        route.</p>
                </body>
                </html>
            `);
            win.document.close();
            win.focus();
            // win.print();
        },
        zoneGeoJSON() {
            const features = (this.plan ? this.plan.trucks : []).map((t) => {
                const b = t.bounds;
                const minLng = b.minLng - ZONE_PAD;
                const maxLng = b.maxLng + ZONE_PAD;
                const minLat = b.minLat - ZONE_PAD;
                const maxLat = b.maxLat + ZONE_PAD;
                return {
                    type: 'Feature',
                    properties: { color: t.color, label: `Run ${t.id}` },
                    geometry: {
                        type: 'Polygon',
                        coordinates: [[
                            [minLng, minLat],
                            [maxLng, minLat],
                            [maxLng, maxLat],
                            [minLng, maxLat],
                            [minLng, minLat],
                        ]],
                    },
                };
            });
            return { type: 'FeatureCollection', features };
        },
        drawZones() {
            if (!this.map || !this.plan || !this.plan.trucks.length) return;
            // addSource/addLayer throw before the style is ready; defer if so.
            if (!this.map.isStyleLoaded()) {
                this.map.once('idle', () => this.drawZones());
                return;
            }
            const data = this.zoneGeoJSON();

            const existing = this.map.getSource('truck-zones');
            if (existing) {
                existing.setData(data);
                return;
            }

            this.map.addSource('truck-zones', { type: 'geojson', data });
            this.map.addLayer({
                id: 'truck-zones-fill',
                type: 'fill',
                source: 'truck-zones',
                paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.40 },
            });
            this.map.addLayer({
                id: 'truck-zones-line',
                type: 'line',
                source: 'truck-zones',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2,
                    'line-dasharray': [2, 1],
                },
            });
            this.map.addLayer({
                id: 'truck-zones-label',
                type: 'symbol',
                source: 'truck-zones',
                layout: {
                    'text-field': ['get', 'label'],
                    'text-size': 13,
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-anchor': 'top',
                    'text-offset': [0, 0.2],
                },
                paint: {
                    'text-color': ['get', 'color'],
                    'text-halo-color': 'rgba(0,0,0,0.7)',
                    'text-halo-width': 1.5,
                },
            });
        },
        removeZoneLayers() {
            if (!this.map) return;
            ['truck-zones-label', 'truck-zones-line', 'truck-zones-fill'].forEach((id) => {
                if (this.map.getLayer(id)) this.map.removeLayer(id);
            });
            if (this.map.getSource('truck-zones')) this.map.removeSource('truck-zones');
        },
        goBack() {
            // Opened in its own tab from the summary, so closing returns the
            // user to the app. Fall back to router history if the browser
            // blocks programmatic close (e.g. opened directly).
            window.close();
            this.$router.back();
        },
    },
};
</script>

<style scoped>
.dispatch-map-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: #111c27;
}

.dm-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
    font-size: 15px;
}

.dm-load-error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 24px;
}

.mle-title {
    color: #e8a838;
    font-size: 20px;
    font-weight: 600;
}

.mle-body {
    color: rgba(255, 255, 255, 0.75);
    font-size: 14px;
    max-width: 420px;
}

.mle-close {
    margin-top: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    padding: 8px 18px;
    font-size: 13px;
    cursor: pointer;
}

.mle-close:hover {
    background: rgba(255, 255, 255, 0.18);
}

/* Header */
.dm-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #1e2d3d;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    z-index: 10;
    flex-shrink: 0;
}

.header-icon {
    width: 32px;
    height: 32px;
    background: #e8a838;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.header-icon svg {
    width: 18px;
    height: 18px;
    fill: #1e2d3d;
}

.header-text h1 {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin: 0;
}

.header-text p {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
    margin-top: 1px;
}

.header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
}

.save-btn {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    padding: 7px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.save-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.18);
}

.save-btn:disabled {
    opacity: 0.55;
    cursor: default;
}

.save-ok {
    font-size: 12px;
    color: #5ddba8;
}

.save-error {
    font-size: 12px;
    color: #f0938a;
    max-width: 240px;
}

.badge-row {
    margin-left: auto;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.badge {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 999px;
    font-weight: 500;
    white-space: nowrap;
}

.back-btn {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
}

.back-btn:hover {
    background: rgba(255, 255, 255, 0.16);
}

.delivery-btn {
    display: block;
    width: 100%;
    background: #e8a838;
    color: #1e2d3d;
    border: none;
    border-radius: 6px;
    padding: 8px 14px;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.delivery-btn:hover {
    background: #f0bb5c;
}

/* Content */
.dm-content {
    display: flex;
    flex: 1;
    overflow: hidden;
}

/* Sidebar */
.sidebar {
    width: 300px;
    flex-shrink: 0;
    background: #16222f;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.sidebar-header {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    flex-shrink: 0;
}

.sidebar-search {
    width: 100%;
    padding: 7px 10px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: #fff;
    font-size: 13px;
    outline: none;
}

.sidebar-search::placeholder {
    color: rgba(255, 255, 255, 0.3);
}

.sidebar-search:focus {
    border-color: rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.1);
}

.well-list {
    overflow-y: auto;
    flex: 1;
}

.well-list::-webkit-scrollbar {
    width: 4px;
}

.well-list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 4px;
}

.well-item {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
    transition: background 0.12s;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border-left: 3px solid transparent;
}

.well-item:hover {
    background: rgba(255, 255, 255, 0.05);
}

.well-item.active {
    background: rgba(255, 255, 255, 0.09);
}

.well-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid;
    flex-shrink: 0;
    margin-top: 3px;
}

.well-info {
    flex: 1;
    min-width: 0;
}

.well-loc {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.well-unassigned {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(240, 147, 138, 0.18);
    border: 1px solid rgba(240, 147, 138, 0.4);
    color: #f0938a;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    vertical-align: middle;
}

.well-dev {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 2px;
}

.well-product {
    font-size: 12px;
    margin-top: 3px;
    font-weight: 600;
}

.well-co {
    font-size: 11px;
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.45);
}

.well-ullage {
    font-size: 11px;
    color: #e8a838;
    margin-top: 3px;
    font-weight: 500;
}

.well-predicted {
    font-size: 11px;
    color: #5ddba8;
    margin-top: 2px;
    font-weight: 500;
}

.well-coords {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.25);
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
}

.no-results {
    padding: 24px 14px;
    color: rgba(255, 255, 255, 0.3);
    font-size: 13px;
    text-align: center;
}

/* Map */
.map-area {
    flex: 1;
    position: relative;
}

.map-canvas {
    position: absolute;
    inset: 0;
}

.style-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 5;
    display: flex;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.style-btn {
    padding: 7px 13px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: none;
}

.style-btn.active {
    background: #1e2d3d;
    color: #fff;
}

.style-btn:not(.active) {
    background: rgba(255, 255, 255, 0.85);
    color: #1e2d3d;
}

.map-legend {
    position: absolute;
    bottom: 36px;
    left: 16px;
    z-index: 5;
    background: rgba(30, 45, 61, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 12px;
    color: #fff;
}

.legend-title {
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 8px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    color: rgba(255, 255, 255, 0.8);
}

.legend-item:last-child {
    margin-bottom: 0;
}

.legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid;
    flex-shrink: 0;
}

.map-empty {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    background: rgba(30, 45, 61, 0.85);
    padding: 16px 24px;
    border-radius: 8px;
}

/* Delivery results panel */
.delivery-results {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 6;
    width: 285px;
    max-height: calc(100% - 32px);
    overflow-y: auto;
    background: rgba(30, 45, 61, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    padding: 12px 14px;
    color: #fff;
}

.dr-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
}

.dr-clear {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    padding: 2px 8px;
    font-size: 11px;
    cursor: pointer;
}

.dr-clear:hover {
    background: rgba(255, 255, 255, 0.18);
}

.dr-summary {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 10px;
}

.dr-unplaced {
    font-size: 11px;
    line-height: 1.4;
    color: #f0938a;
    background: rgba(240, 147, 138, 0.12);
    border: 1px solid rgba(240, 147, 138, 0.3);
    border-radius: 6px;
    padding: 6px 8px;
    margin-bottom: 8px;
}

.dr-truck {
    padding: 6px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.dr-truck-top {
    display: flex;
    align-items: center;
    gap: 8px;
}

.dr-swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
}

.dr-truck-name {
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.dr-print {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    flex-shrink: 0;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.55);
    cursor: pointer;
}

.dr-print svg {
    width: 12px;
    height: 12px;
    fill: currentColor;
}

.dr-print:hover {
    background: rgba(232, 168, 56, 0.18);
    border-color: #e8a838;
    color: #e8a838;
}

.dr-truck-cap {
    font-size: 11px;
    font-weight: 600;
    color: #e8a838;
    margin-left: auto;
    flex-shrink: 0;
}

.dr-truck-meta {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 2px;
    padding-left: 20px;
}

.dr-products {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
    padding-left: 20px;
}

.dr-prod {
    font-size: 10px;
    line-height: 1.5;
    padding: 1px 7px;
    border-left: 3px solid transparent;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.75);
    white-space: nowrap;
}

.dr-overcap {
    font-size: 11px;
    margin-top: 4px;
    padding-left: 20px;
}

.dr-warn {
    color: #f0938a;
}

/* Delivery Parameters modal */
.dm-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
}

.dm-modal {
    width: 380px;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    background: #1e2d3d;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    padding: 22px 24px;
    color: #fff;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
}

/* Fleet editor: capacity + quantity per truck size. */
.fleet-table {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.fleet-head,
.fleet-row {
    display: grid;
    grid-template-columns: 1fr 90px 26px;
    gap: 8px;
    align-items: center;
}

.fleet-head span {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.4);
}

.fleet-remove {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
}

.fleet-remove:hover:not(:disabled) {
    background: rgba(240, 147, 138, 0.2);
    border-color: rgba(240, 147, 138, 0.5);
    color: #f0938a;
}

.fleet-remove:disabled {
    opacity: 0.3;
    cursor: default;
}

.fleet-add {
    margin-top: 8px;
    background: transparent;
    border: 1px dashed rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.7);
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    width: 100%;
}

.fleet-add:hover {
    border-color: #e8a838;
    color: #e8a838;
}

.dm-modal h3 {
    margin: 0 0 6px 0;
    font-size: 17px;
}

.modal-sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 16px;
}

.modal-label {
    display: block;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    margin: 14px 0 6px;
}

.modal-label:first-of-type {
    margin-top: 0;
}

.modal-optional {
    color: rgba(255, 255, 255, 0.4);
    font-style: italic;
}

.modal-input {
    width: 100%;
    padding: 9px 11px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: #fff;
    font-size: 14px;
    outline: none;
}

.modal-input:focus {
    border-color: #e8a838;
}

.modal-slider {
    width: 100%;
    margin: 2px 0 0;
    accent-color: #e8a838;
    cursor: pointer;
}

.slider-ends {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 2px;
}

.modal-hint {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    margin-top: 10px;
}

.modal-hint strong {
    color: #e8a838;
}

.modal-warn {
    font-size: 12px;
    color: #e8a838;
    margin-top: 8px;
}

.modal-error {
    font-size: 12px;
    color: #f0938a;
    margin-top: 10px;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.btn-secondary,
.btn-primary {
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid transparent;
}

.btn-secondary {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.18);
}

.btn-secondary:hover {
    background: rgba(255, 255, 255, 0.16);
}

.btn-primary {
    background: #e8a838;
    color: #1e2d3d;
    font-weight: 600;
}

.btn-primary:hover {
    background: #f0bb5c;
}

/* Mobile */
@media (max-width: 640px) {
    .dm-header {
        flex-wrap: wrap;
    }

    .header-actions {
        width: 100%;
        margin-left: 0;
        justify-content: flex-end;
    }

    .dm-modal {
        width: calc(100vw - 32px);
    }

    .dm-content {
        flex-direction: column;
    }

    .sidebar {
        width: 100%;
        height: 220px;
        border-right: none;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        order: 2;
    }

    .map-area {
        order: 1;
        min-height: 0;
        flex: 1;
    }
}
</style>

<!-- Mapbox popup styling (not scoped: popup DOM is rendered outside this component). -->
<style>
.dm-marker {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 3px solid;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25), 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: transform 0.15s;
}

.dm-marker:hover {
    transform: scale(1.3);
}

.dm-marker.active-marker {
    transform: scale(1.4);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 0.6);
}

.mapboxgl-popup-content {
    background: #1e2d3d !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 10px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4) !important;
    padding: 0 !important;
    color: #fff;
}

.mapboxgl-popup-tip {
    border-top-color: #1e2d3d !important;
    border-bottom-color: #1e2d3d !important;
}

.mapboxgl-popup-close-button {
    color: rgba(255, 255, 255, 0.5) !important;
    font-size: 18px !important;
    padding: 6px 10px !important;
}

.mapboxgl-popup-close-button:hover {
    color: #fff !important;
    background: transparent !important;
}

.popup-inner {
    padding: 14px 16px;
    min-width: 220px;
}

.popup-location {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
}

.popup-device {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 8px;
}

.popup-company {
    font-size: 12px;
    font-weight: 600;
}

.popup-operator {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    margin-top: 2px;
}

.popup-ullage {
    font-size: 12px;
    font-weight: 600;
    color: #e8a838;
    margin-top: 6px;
}

.popup-predicted {
    font-size: 12px;
    font-weight: 600;
    color: #5ddba8;
    margin-top: 2px;
}

/* Distance scale bar — make it legible on the dark/satellite map. */
.mapboxgl-ctrl-scale {
    background: rgba(30, 45, 61, 0.85);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-top: none;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
    padding: 0 6px;
}

.popup-coords {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.3);
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
}
</style>
